import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { useEffect, useRef, useState } from 'react';
import { Discount } from '../../../../models/UserPayment';
import { ErrorType, recordError } from '../../../../models/apiWrappers/api/exceptions';
import { useMst } from '../../../../models/rootModel';
import { PlanDataExtracted } from '../../../../types/plan';
import { useAnalytics } from '../../../../utils/analytics';
import { sendGTMEvent } from '../../../../utils/googleTagManager';
import { calculateAmountWithDiscount, calculatePurchasePrice, formatAmount } from '../../../../utils/price/price.utils';
import { markConversion } from '../../../../utils/tapfiliate';
import { StepPropsV2 } from '../../../common/v2/BaseStepV2.types';
import { PaymentMethod } from '../../PaymentStep/paymentStep.types';
import { CreditCardFieldState } from '../SignupWithPayment.type';
import { getDocumentId } from '../../../../utils/utilsFunctions';

interface UsePaymentParams {
  currentPlan: PlanDataExtracted;
  discount: Discount | null;
  paymentMethod: PaymentMethod;
  stepProps: StepPropsV2 & { title?: string };
}

export const usePayment = ({ currentPlan, discount, paymentMethod, stepProps }: UsePaymentParams) => {
  const { UserPayment, UserDiscoveryResponse, Step } = useMst();
  const stripe = useStripe();
  const analytics = useAnalytics();
  const elements = useElements();

  const [isPaymentBeingProcessed, setIsPaymentBeingProcessed] = useState<boolean>(false);
  const [isCardInfoCorrect, setIsCardInfoCorrect] = useState(false);
  const [isAnyCredidCardFieldChanged, setIsAnyCredidCardFieldChanged] = useState(false);
  const [paymentErrorAlertText, setPaymentErrorAlertText] = useState('');

  const [cardNumberState, setCardNumberState] = useState<CreditCardFieldState>({
    state: 'unknown',
    error: false,
  });
  const [cardExpState, setCardExpState] = useState<CreditCardFieldState>({
    state: 'unknown',
    error: false,
  });
  const [cardCvcState, setCardCvcState] = useState<CreditCardFieldState>({
    state: 'unknown',
    error: false,
  });

  const analyticsObject = useRef({
    ...UserDiscoveryResponse.utms,
    stepId: stepProps.id,
    stepType: stepProps.type,
    stepIndex: stepProps.stepIndex,
    stepTitle: stepProps.title,
    origin: Step.origin,
    userID: UserDiscoveryResponse.accountId,
  });

  const sendPaymentAnalytics = () => {
    const discountAmount = discount?.coupon?.duration
      ? (currentPlan.sanitizedAmount / 100) * (discount.coupon?.percentOff / 100)
      : 0;

    const amount = calculateAmountWithDiscount(currentPlan.sanitizedAmount, discount);

    const currency = 'USD';

    const promoCode = UserPayment.appliedCouponCode || 'NA';
    sendGTMEvent({
      event: 'purchase',
      user_id: UserDiscoveryResponse.accountId,
      phone: UserDiscoveryResponse.userPhoneNumber,
      full_name: UserDiscoveryResponse.userName,
      email: UserDiscoveryResponse.userEmail,
      ecommerce: {
        transaction_id: UserPayment.purchaseId,
        value: calculatePurchasePrice(amount),
        currency, // the currency code, e.g. “USD”
        tax: 0,
        coupon: promoCode, // Coupon code, if applied
        first_time_client: 'yes',
        transaction_method: paymentMethod,
        user_id: UserDiscoveryResponse.accountId,
        items: [
          {
            item_id: UserPayment.planId,
            item_name: 'Premium',
            item_brand: 'Circles',
            coupon: promoCode,
            currency,
            discount: discountAmount.toFixed(2),
            price: formatAmount(currentPlan.sanitizedAmount),
            quantity: 1,
          },
        ],
      },
    });

    sendGTMEvent({
      event: 'awin.dl.ready',
      transactionTotal: formatAmount(amount),
      transactionCurrency: currency,
      transactionID: UserPayment.purchaseId,
      transactionPromoCode: promoCode,
    });

    markConversion(UserDiscoveryResponse.accountId ?? 'unknown', {
      amountInCents: currentPlan.sanitizedAmount,
      currencyIsoCode: currentPlan?.sanitizedCurrency,
    });
  };

  const processPayment = async (
    ev?: PaymentRequestPaymentMethodEvent
  ): Promise<
    | {
        error: false;
      }
    | {
        error: true;
        customErrorMessage?: string;
      }
  > => {
    try {
      const choosenPaymentMethod = ev ? ev.walletName : 'creditCard';
      analytics.track('pay confirmation click', {
        paymentType: choosenPaymentMethod,
        productSku: currentPlan.sanitizedId,
        price: calculateAmountWithDiscount(currentPlan.sanitizedAmount, discount),
        planPrice: currentPlan.sanitizedAmount,
      });
      setIsPaymentBeingProcessed(true);
      if (!stripe || !elements) return { error: true };
      await UserPayment.getStripeSecretOnly();

      let result;
      if (ev) {
        result = await stripe.confirmCardSetup(UserPayment.clientSecret, {
          payment_method: ev.paymentMethod.id,
        });
        // this closes the browser’s payment interface
      } else {
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) return { error: true };
        result = await stripe.confirmCardSetup(UserPayment.clientSecret, {
          payment_method: {
            card: cardElement,
          },
        });
      }

      if (result.error) {
        recordError(ErrorType.HANDLED, result.error);
        analytics.track(`view payment error`, {
          ...analyticsObject,
          error: result.error,
          declineReason: result.error.code,
          productSku: currentPlan.sanitizedId,
          paymentType: choosenPaymentMethod,
        });

        return { error: true, customErrorMessage: result.error.message || 'An error occurred' };
      }

      if (result.setupIntent.status === 'succeeded') {
        const { payment_method } = result.setupIntent;

        try {
          const res = await UserPayment.processPayment({
            paymentMethodId: payment_method as string,
            priceId: currentPlan.sanitizedId,
            discount: UserPayment.discount,
            meta: { choosenPaymentMethod },
            onboardingDocumentId: getDocumentId(),
          });

          if (res.success) {
            ev?.complete('success');
            if (!res.isMagicDoorPurchase) {
              sendPaymentAnalytics();
            }
            return { error: false };
          } else {
            throw new Error(res.data?._uiNotice?.title ?? 'Error, please try another card', { cause: res.data });
          }
        } catch (error: any) {
          recordError(ErrorType.HANDLED, error);

          analytics.track(`view payment error`, {
            ...analyticsObject,
            paymentType: choosenPaymentMethod,
            productSku: currentPlan.sanitizedId,
            declineReason: error?.cause?.code ?? 'unknown',
            error,
          });
          setPaymentErrorAlertText(error.message);
          UserPayment.getStripePlans();
          UserPayment.getStripeSecretOnly();
          return { error: true, customErrorMessage: error.message || 'An error occurred' };
        }
      }
      return { error: true };
    } catch (error) {
      recordError(ErrorType.FATAL, error);
      handlePotentialExpressPaymentFail(ev);
      return { error: true };
    } finally {
      setIsPaymentBeingProcessed(false);
    }
  };

  const recordCreditCardFieldChange = () => {
    setIsAnyCredidCardFieldChanged(true);
  };

  const validateCreditCardForm = () => {
    if (isCardInfoCorrect) {
      return true;
    }
    if (cardNumberState.state !== 'valid') {
      setCardNumberState({ state: 'invalid', error: true });
    }
    if (cardExpState.state !== 'valid') {
      setCardExpState({ state: 'invalid', error: true });
    }
    if (cardCvcState.state !== 'valid') {
      setCardCvcState({ state: 'invalid', error: true });
    }

    return false;
  };

  const handlePotentialExpressPaymentFail = (paymentRequestEvent?: PaymentRequestPaymentMethodEvent) => {
    paymentRequestEvent?.complete('fail');
  };

  useEffect(() => {
    if (cardNumberState.state === 'valid' && cardExpState.state === 'valid' && cardCvcState.state === 'valid') {
      setIsCardInfoCorrect(true);
    } else {
      setIsCardInfoCorrect(false);
    }
  }, [cardNumberState, cardExpState, cardCvcState]);

  return {
    processPayment,
    isPaymentBeingProcessed,
    paymentErrorAlertText,
    setPaymentErrorAlertText,
    isAnyCredidCardFieldChanged,
    recordCreditCardFieldChange,
    cardNumberState,
    setCardNumberState,
    cardExpState,
    setCardExpState,
    cardCvcState,
    setCardCvcState,
    validateCreditCardForm,
    handlePotentialExpressPaymentFail,
  };
};
